/**
 * Sketch constraint solver — a small damped Gauss-Newton (Levenberg-
 * Marquardt) solver over sketch entity degrees of freedom.
 *
 * Entities flatten to a variable vector (line: ax ay bx by; rect: minx miny
 * maxx maxy; circle: cx cy r; arc: cx cy r sa ea), constraints contribute
 * residual functions, and the solver drives residuals to zero while a weak
 * regularization term keeps the solution NEAR the drawn geometry — so
 * underconstrained sketches (the normal case) move as little as possible.
 *
 * Runs at evaluation time: the kernel receives SOLVED entities, and the
 * feature's content hash covers them, so editing a dimension regenerates
 * downstream features exactly like any other parametric edit.
 */

import type { SketchConstraint, SketchEntity, SketchPointRef, Vec2 } from '../timeline/types';

const MAX_ITERATIONS = 120;
const RESIDUAL_TOL = 1e-9;

export class ConstraintError extends Error {
  override name = 'ConstraintError';
}

interface EntitySlot {
  readonly kind: SketchEntity['kind'];
  /** Index of this entity's first variable in the DOF vector. */
  readonly base: number;
  readonly count: number;
}

function flatten(entities: readonly SketchEntity[]): { x: number[]; slots: EntitySlot[] } {
  const x: number[] = [];
  const slots: EntitySlot[] = [];
  for (const e of entities) {
    const base = x.length;
    switch (e.kind) {
      case 'line':
        x.push(e.a[0], e.a[1], e.b[0], e.b[1]);
        break;
      case 'rect':
        x.push(e.min[0], e.min[1], e.max[0], e.max[1]);
        break;
      case 'circle':
        x.push(e.center[0], e.center[1], e.radius);
        break;
      case 'ellipse':
        x.push(e.center[0], e.center[1], e.rx, e.ry);
        break;
      case 'arc':
        x.push(e.center[0], e.center[1], e.radius, e.startAngle, e.endAngle);
        break;
      case 'spline':
        for (const p of e.points) x.push(p[0], p[1]);
        break;
      case 'text':
      case 'poly':
        break; // fixed geometry — no degrees of freedom
    }
    slots.push({ kind: e.kind, base, count: x.length - base });
  }
  return { x, slots };
}

function unflatten(entities: readonly SketchEntity[], slots: EntitySlot[], x: number[]): SketchEntity[] {
  return entities.map((e, i) => {
    const b = slots[i]!.base;
    switch (e.kind) {
      case 'line':
        return { kind: 'line', a: [x[b]!, x[b + 1]!], b: [x[b + 2]!, x[b + 3]!] } as const;
      case 'rect':
        return {
          kind: 'rect',
          min: [Math.min(x[b]!, x[b + 2]!), Math.min(x[b + 1]!, x[b + 3]!)],
          max: [Math.max(x[b]!, x[b + 2]!), Math.max(x[b + 1]!, x[b + 3]!)],
        } as const;
      case 'circle':
        return { kind: 'circle', center: [x[b]!, x[b + 1]!], radius: Math.abs(x[b + 2]!) } as const;
      case 'ellipse':
        return {
          kind: 'ellipse',
          center: [x[b]!, x[b + 1]!],
          rx: Math.abs(x[b + 2]!),
          ry: Math.abs(x[b + 3]!),
        } as const;
      case 'arc':
        return {
          kind: 'arc',
          center: [x[b]!, x[b + 1]!],
          radius: Math.abs(x[b + 2]!),
          startAngle: x[b + 3]!,
          endAngle: x[b + 4]!,
        } as const;
      case 'spline':
        return {
          kind: 'spline',
          points: e.points.map((_p, k) => [x[b + k * 2]!, x[b + k * 2 + 1]!] as const),
          ...(e.closed ? { closed: true } : {}),
        } as const;
      case 'text':
      case 'poly':
        return e; // no DOFs — passes through unchanged
    }
  });
}

/** Point position for a SketchPointRef given the DOF vector. */
function pointOf(refp: SketchPointRef, slots: EntitySlot[], x: number[]): Vec2 {
  const slot = slots[refp.entity];
  if (!slot) throw new ConstraintError(`Constraint references missing entity #${refp.entity}`);
  const b = slot.base;
  const bad = (): never => {
    throw new ConstraintError(`Entity #${refp.entity} (${slot.kind}) has no point "${refp.point}"`);
  };
  switch (slot.kind) {
    case 'line':
      return refp.point === 'a' ? [x[b]!, x[b + 1]!]
        : refp.point === 'b' ? [x[b + 2]!, x[b + 3]!]
        : refp.point === 'mid' ? [(x[b]! + x[b + 2]!) / 2, (x[b + 1]! + x[b + 3]!) / 2]
        : bad();
    case 'rect':
      return refp.point === 'min' ? [x[b]!, x[b + 1]!]
        : refp.point === 'max' ? [x[b + 2]!, x[b + 3]!]
        : refp.point === 'mid' ? [(x[b]! + x[b + 2]!) / 2, (x[b + 1]! + x[b + 3]!) / 2]
        : bad();
    case 'circle':
    case 'ellipse':
      return refp.point === 'center' ? [x[b]!, x[b + 1]!] : bad();
    case 'arc':
      return refp.point === 'center' ? [x[b]!, x[b + 1]!]
        : refp.point === 'a'
          ? [x[b]! + x[b + 2]! * Math.cos(x[b + 3]!), x[b + 1]! + x[b + 2]! * Math.sin(x[b + 3]!)]
          : refp.point === 'b'
            ? [x[b]! + x[b + 2]! * Math.cos(x[b + 4]!), x[b + 1]! + x[b + 2]! * Math.sin(x[b + 4]!)]
            : bad();
    case 'spline':
      // 'a' = first through-point, 'b' = last.
      return refp.point === 'a' ? [x[b]!, x[b + 1]!]
        : refp.point === 'b' ? [x[b + slot.count - 2]!, x[b + slot.count - 1]!]
        : bad();
    case 'text':
    case 'poly':
      return bad(); // fixed geometry has no constrainable points
  }
}

/** Direction (unnormalized) of a line-like entity; rect uses its diagonal. */
function directionOf(entity: number, slots: EntitySlot[], x: number[]): Vec2 {
  const slot = slots[entity];
  if (!slot) throw new ConstraintError(`Constraint references missing entity #${entity}`);
  if (slot.kind !== 'line') {
    throw new ConstraintError(`Entity #${entity} is a ${slot.kind}; needs a line`);
  }
  const b = slot.base;
  return [x[b + 2]! - x[b]!, x[b + 3]! - x[b + 1]!];
}

function sizeOf(entity: number, slots: EntitySlot[], x: number[]): number {
  const slot = slots[entity];
  if (!slot) throw new ConstraintError(`Constraint references missing entity #${entity}`);
  const b = slot.base;
  if (slot.kind === 'line') return Math.hypot(x[b + 2]! - x[b]!, x[b + 3]! - x[b + 1]!);
  if (slot.kind === 'circle' || slot.kind === 'arc') return Math.abs(x[b + 2]!);
  throw new ConstraintError(`Entity #${entity} (${slot.kind}) has no length/radius for "equal"`);
}

/** Build the residual vector for the constraint set at DOF vector x. */
function residuals(
  constraints: readonly SketchConstraint[],
  slots: EntitySlot[],
  x: number[],
): number[] {
  const r: number[] = [];
  for (const c of constraints) {
    switch (c.kind) {
      case 'coincident': {
        const pa = pointOf(c.a, slots, x);
        const pb = pointOf(c.b, slots, x);
        r.push(pa[0] - pb[0], pa[1] - pb[1]);
        break;
      }
      case 'horizontal': {
        const d = directionOf(c.entity, slots, x);
        r.push(d[1]);
        break;
      }
      case 'vertical': {
        const d = directionOf(c.entity, slots, x);
        r.push(d[0]);
        break;
      }
      case 'parallel': {
        const da = directionOf(c.a, slots, x);
        const db = directionOf(c.b, slots, x);
        r.push(da[0] * db[1] - da[1] * db[0]); // cross = 0
        break;
      }
      case 'perpendicular': {
        const da = directionOf(c.a, slots, x);
        const db = directionOf(c.b, slots, x);
        r.push(da[0] * db[0] + da[1] * db[1]); // dot = 0
        break;
      }
      case 'equal':
        r.push(sizeOf(c.a, slots, x) - sizeOf(c.b, slots, x));
        break;
      case 'distance': {
        const pa = pointOf(c.a, slots, x);
        const pb = pointOf(c.b, slots, x);
        r.push(Math.hypot(pa[0] - pb[0], pa[1] - pb[1]) - numeric(c.value, 'distance'));
        break;
      }
      case 'radius': {
        const slot = slots[c.entity];
        if (!slot) throw new ConstraintError(`Constraint references missing entity #${c.entity}`);
        if (slot.kind !== 'circle' && slot.kind !== 'arc') {
          throw new ConstraintError(`Radius constraint needs a circle/arc (entity #${c.entity})`);
        }
        r.push(Math.abs(x[slot.base + 2]!) - numeric(c.value, 'radius'));
        break;
      }
      case 'fixed': {
        const p = pointOf(c.point, slots, x);
        r.push(p[0] - c.at[0], p[1] - c.at[1]);
        break;
      }
      case 'tangent': {
        // Line tangent to circle/arc (either argument order): distance from
        // the center to the infinite line equals the radius.
        const [lineIdx, circIdx] =
          slots[c.a]?.kind === 'line' ? [c.a, c.b] : [c.b, c.a];
        const ls = slots[lineIdx];
        const cs = slots[circIdx];
        if (!ls || !cs) throw new ConstraintError('Tangent references a missing entity');
        if (ls.kind !== 'line' || (cs.kind !== 'circle' && cs.kind !== 'arc')) {
          throw new ConstraintError('Tangent needs one line and one circle/arc');
        }
        const ax = x[ls.base]!, ay = x[ls.base + 1]!;
        const bx = x[ls.base + 2]!, by = x[ls.base + 3]!;
        const cx = x[cs.base]!, cy = x[cs.base + 1]!;
        const rad = Math.abs(x[cs.base + 2]!);
        const len = Math.hypot(bx - ax, by - ay) || 1e-9;
        const cross = (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
        r.push(Math.abs(cross) / len - rad);
        break;
      }
      case 'concentric': {
        for (const idx of [c.a, c.b]) {
          const s = slots[idx];
          if (!s || (s.kind !== 'circle' && s.kind !== 'arc' && s.kind !== 'ellipse')) {
            throw new ConstraintError('Concentric needs circles/arcs/ellipses');
          }
        }
        const A = slots[c.a]!, B = slots[c.b]!;
        r.push(x[A.base]! - x[B.base]!, x[A.base + 1]! - x[B.base + 1]!);
        break;
      }
      case 'symmetric': {
        // Mirror of point a across line `about` coincides with point b.
        const ls = slots[c.about];
        if (!ls || ls.kind !== 'line') throw new ConstraintError('Symmetric needs a mirror LINE');
        const pa = pointOf(c.a, slots, x);
        const pb = pointOf(c.b, slots, x);
        const ax = x[ls.base]!, ay = x[ls.base + 1]!;
        const dx = x[ls.base + 2]! - ax, dy = x[ls.base + 3]! - ay;
        const len2 = dx * dx + dy * dy || 1e-9;
        const t = ((pa[0] - ax) * dx + (pa[1] - ay) * dy) / len2;
        const mx = 2 * (ax + t * dx) - pa[0];
        const my = 2 * (ay + t * dy) - pa[1];
        r.push(mx - pb[0], my - pb[1]);
        break;
      }
    }
  }
  return r;
}

/** Dimension values must be resolved (expressions → numbers) before solving. */
function numeric(v: number | { $expr: string }, what: string): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) {
    throw new ConstraintError(`Unresolved ${what} value (expression not evaluated?)`);
  }
  return v;
}

/** Solve (JᵀJ + λI) dx = −Jᵀr with plain Gaussian elimination. */
function solveNormal(J: number[][], r: number[], lambda: number, n: number): number[] {
  const A: number[][] = Array.from({ length: n }, () => new Array<number>(n + 1).fill(0));
  for (let i = 0; i < n; i++) {
    A[i]![i] = lambda;
    for (let j = 0; j < n; j++) {
      let s = 0;
      for (let k = 0; k < J.length; k++) s += J[k]![i]! * J[k]![j]!;
      A[i]![j]! += s;
    }
    let b = 0;
    for (let k = 0; k < J.length; k++) b -= J[k]![i]! * r[k]!;
    A[i]![n] = b;
  }
  for (let col = 0; col < n; col++) {
    let pivot = col;
    for (let row = col + 1; row < n; row++) {
      if (Math.abs(A[row]![col]!) > Math.abs(A[pivot]![col]!)) pivot = row;
    }
    [A[col], A[pivot]] = [A[pivot]!, A[col]!];
    const p = A[col]![col]!;
    if (Math.abs(p) < 1e-14) continue; // rank-deficient direction — leave 0
    for (let row = 0; row < n; row++) {
      if (row === col) continue;
      const f = A[row]![col]! / p;
      for (let k = col; k <= n; k++) A[row]![k]! -= f * A[col]![k]!;
    }
  }
  const dx = new Array<number>(n).fill(0);
  for (let i = 0; i < n; i++) {
    const p = A[i]![i]!;
    dx[i] = Math.abs(p) < 1e-14 ? 0 : A[i]![n]! / p;
  }
  return dx;
}

export interface SolveResult {
  entities: SketchEntity[];
  converged: boolean;
  maxResidual: number;
  iterations: number;
}

/**
 * Solve the sketch. Throws ConstraintError on malformed constraints
 * (bad entity refs); returns converged=false when the system is
 * inconsistent (e.g. contradictory dimensions) — callers surface that as
 * a feature error with the residual magnitude.
 */
export function solveSketch(
  entities: readonly SketchEntity[],
  constraints: readonly SketchConstraint[],
): SolveResult {
  if (constraints.length === 0) {
    return { entities: [...entities], converged: true, maxResidual: 0, iterations: 0 };
  }
  const { x, slots } = flatten(entities);
  const n = x.length;

  let r = residuals(constraints, slots, x);
  let cost = r.reduce((s, v) => s + v * v, 0);
  let lambda = 1e-3;
  let iterations = 0;

  for (; iterations < MAX_ITERATIONS; iterations++) {
    const maxR = r.reduce((m, v) => Math.max(m, Math.abs(v)), 0);
    if (maxR < RESIDUAL_TOL) break;

    // Numeric Jacobian. Underconstrained sketches (the normal case) give a
    // rank-deficient J; the λI damping makes each step the MINIMUM-NORM one,
    // so starting from the drawn geometry we converge to the nearest solution
    // — no explicit anchoring needed (an anchor would keep residuals ≠ 0).
    const m = r.length;
    const J: number[][] = Array.from({ length: m }, () => new Array<number>(n).fill(0));
    for (let j = 0; j < n; j++) {
      const h = Math.max(1e-7, Math.abs(x[j]!) * 1e-7);
      const saved = x[j]!;
      x[j] = saved + h;
      const rp = residuals(constraints, slots, x);
      x[j] = saved;
      for (let i = 0; i < m; i++) J[i]![j] = (rp[i]! - r[i]!) / h;
    }

    // LM step with adaptive damping.
    let improved = false;
    for (let attempt = 0; attempt < 10; attempt++) {
      const dx = solveNormal(J, r, lambda, n);
      const xTrial = x.map((v, i) => v + dx[i]!);
      const rTrial = residuals(constraints, slots, xTrial);
      const costTrial = rTrial.reduce((s, v) => s + v * v, 0);
      if (costTrial < cost || dx.every((d) => Math.abs(d) < 1e-12)) {
        for (let i = 0; i < n; i++) x[i] = xTrial[i]!;
        r = rTrial;
        cost = costTrial;
        lambda = Math.max(1e-10, lambda / 3);
        improved = true;
        break;
      }
      lambda *= 10;
    }
    if (!improved) break; // stuck — report best effort
  }

  const maxResidual = r.reduce((m, v) => Math.max(m, Math.abs(v)), 0);
  return {
    entities: unflatten(entities, slots, x),
    converged: maxResidual < 1e-6,
    maxResidual,
    iterations,
  };
}

/** Human label for a constraint (context menus, properties panel). */
export function constraintLabel(c: SketchConstraint): string {
  switch (c.kind) {
    case 'coincident': return `Coincident #${c.a.entity}.${c.a.point} ↔ #${c.b.entity}.${c.b.point}`;
    case 'horizontal': return `Horizontal #${c.entity}`;
    case 'vertical': return `Vertical #${c.entity}`;
    case 'parallel': return `Parallel #${c.a} ∥ #${c.b}`;
    case 'perpendicular': return `Perpendicular #${c.a} ⊥ #${c.b}`;
    case 'equal': return `Equal #${c.a} = #${c.b}`;
    case 'distance': {
      const v = typeof c.value === 'number' ? String(c.value) : c.value.$expr;
      return `Distance #${c.a.entity}.${c.a.point} ↔ #${c.b.entity}.${c.b.point} = ${v}`;
    }
    case 'radius': {
      const v = typeof c.value === 'number' ? String(c.value) : c.value.$expr;
      return `Radius #${c.entity} = ${v}`;
    }
    case 'fixed': return `Fixed #${c.point.entity}.${c.point.point} @ (${c.at[0]}, ${c.at[1]})`;
    case 'tangent': return `Tangent #${c.a} ⌒ #${c.b}`;
    case 'concentric': return `Concentric #${c.a} ⊙ #${c.b}`;
    case 'symmetric': return `Symmetric #${c.a.entity}.${c.a.point} ⋈ #${c.b.entity}.${c.b.point} about #${c.about}`;
  }
}
